import { useTranslation } from 'react-i18next';

export default function LendingHowItWorksSection() {
  const { t } = useTranslation();
  
  return (
    <section className="py-20 bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('lending.howItWorks.title')}
          </h2>
        </div>

        <div className="bg-gray-900 rounded-2xl p-8 md:p-12 border border-gray-700 mb-12">
          {/* Custom Infographic */}
          <div className="mb-8">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
                {/* Step 1 */}
                <div className="relative">
                  <div className="bg-gradient-to-br from-green-500 to-teal-500 rounded-2xl p-8 text-center">
                    <div className="w-20 h-20 mx-auto mb-4 bg-white rounded-full flex items-center justify-center">
                      <i className="ri-wallet-3-line text-4xl text-green-600"></i>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{t('lending.howItWorks.step1.title')}</h3>
                    <p className="text-white/90 text-sm">
                      {t('lending.howItWorks.step1.desc')}
                    </p>
                  </div>
                  {/* Arrow */}
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-4xl text-teal-400"></i>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="relative">
                  <div className="bg-gradient-to-br from-teal-500 to-blue-500 rounded-2xl p-8 text-center">
                    <div className="w-20 h-20 mx-auto mb-4 bg-white rounded-full flex items-center justify-center">
                      <i className="ri-line-chart-line text-4xl text-teal-600"></i>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{t('lending.howItWorks.step2.title')}</h3>
                    <p className="text-white/90 text-sm">
                      {t('lending.howItWorks.step2.desc')}
                    </p>
                  </div>
                  {/* Arrow */}
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-4xl text-blue-400"></i>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="relative">
                  <div className="bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl p-8 text-center">
                    <div className="w-20 h-20 mx-auto mb-4 bg-white rounded-full flex items-center justify-center">
                      <i className="ri-money-dollar-circle-line text-4xl text-blue-600"></i>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{t('lending.howItWorks.step3.title')}</h3>
                    <p className="text-white/90 text-sm">
                      {t('lending.howItWorks.step3.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Badge */}
              <div className="mt-8 text-center">
                <div className="inline-block bg-gradient-to-r from-green-500 to-teal-500 text-white px-6 py-3 rounded-full font-bold text-lg shadow-lg">
                  <i className="ri-time-line mr-2"></i>
                  {t('lending.howItWorks.badge')}
                </div>
              </div>
            </div>
          </div>

          <p className="text-gray-300 leading-relaxed mb-6">
            {t('lending.howItWorks.desc')}
          </p>
        </div>

        {/* Advantage Card */}
        <div className="bg-gradient-to-br from-green-900/30 to-teal-900/30 rounded-2xl p-8 md:p-12 border border-green-700">
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center">
                <i className="ri-trophy-line text-3xl text-white"></i>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-white mb-4">
                {t('lending.howItWorks.advantage.title')}
              </h3>
              {/* Visual Representation with Text and Icons */}
              <div className="mb-6 bg-gradient-to-r from-blue-900/50 to-green-900/50 rounded-xl p-4 md:p-8 border border-blue-500/30">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-8">
                  {/* Initial Investment */}
                  <div className="flex-1 text-center w-full">
                    <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-blue-600 rounded-full mb-3 md:mb-4">
                      <i className="ri-line-chart-line text-3xl md:text-4xl text-white"></i>
                    </div>
                    <div className="text-xs md:text-sm text-gray-400 mb-2">Initial Investment</div>
                    <div className="text-2xl md:text-3xl font-bold text-white">1 BTC</div>
                    <div className="text-xs text-white mt-1">November 2023: 1 BTC = 34,500 USD</div>
                  </div>

                  {/* Arrow */}
                  <div className="flex-shrink-0 rotate-90 md:rotate-0">
                    <i className="ri-arrow-right-line text-3xl md:text-4xl text-green-400"></i>
                  </div>

                  {/* Annual Return */}
                  <div className="flex-1 text-center w-full">
                    <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-green-600 rounded-full mb-3 md:mb-4">
                      <i className="ri-percent-line text-3xl md:text-4xl text-white"></i>
                    </div>
                    <div className="text-xs md:text-sm text-gray-400 mb-2">+15% Annual Return</div>
                    <div className="text-lg md:text-xl font-bold text-green-400">After 1 Year</div>
                    <div className="text-2xl md:text-3xl font-bold text-white mt-2">1.15 BTC</div>
                    <div className="text-xs text-white mt-1">November 2024: 1 BTC = 96,500 USD</div>
                  </div>

                  {/* Arrow */}
                  <div className="flex-shrink-0 rotate-90 md:rotate-0">
                    <i className="ri-arrow-right-line text-3xl md:text-4xl text-yellow-400"></i>
                  </div>

                  {/* Profit */}
                  <div className="flex-1 text-center w-full">
                    <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-yellow-600 rounded-full mb-3 md:mb-4">
                      <i className="ri-money-dollar-circle-line text-3xl md:text-4xl text-white"></i>
                    </div>
                    <div className="text-xs md:text-sm text-gray-400 mb-2">Profit</div>
                    <div className="text-xl md:text-2xl font-bold text-yellow-400">+0.15 BTC</div>
                    <div className="text-2xl md:text-3xl font-bold text-red-500 mt-2">+76,475 USD</div>
                  </div>
                </div>
              </div>
              <p className="text-gray-300 leading-relaxed">
                {t('lending.howItWorks.advantage.desc')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
